import React, {
  createContext, useContext, useEffect, useRef, useState, useCallback
} from "react";
import api from "../api";

const ActiveStoresContext = createContext(null);

// ---- Config ----
const CACHE_KEY = "bf_active_stores_v1";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes (tweak as you like)
const ENDPOINT = "/stores/active";

// de-dup in-flight requests (prevents double fetch & StrictMode duplicates)
let inFlight = null;

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null; // { data, ts }
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {}
}

function isImage(url = "") {
  return /\.(png|jpe?g|gif|webp|avif|svg)$/i.test((url.split("?")[0] || ""));
}
function preloadOne(src) {
  return new Promise((res) => {
    const img = new Image();
    img.onload = res;
    img.onerror = res;
    img.src = src;
  });
}
async function preloadLogos(stores = []) {
  const urls = [];
  for (const s of stores) {
    if (s?.businessLogo && isImage(s.businessLogo)) urls.push(s.businessLogo);
  }
  await Promise.allSettled(urls.map(preloadOne));
}

export function ActiveStoresProvider({ children }) {
  const cached = readCache();
  const [stores, setStores] = useState(cached?.data || []);
  const [loading, setLoading] = useState(!cached?.data);
  const [error, setError] = useState(null);
  const tsRef = useRef(cached?.ts || 0);

  const fetchActiveStores = useCallback(async ({ revalidate = false } = {}) => {
    const now = Date.now();
    const fresh = now - tsRef.current < CACHE_TTL_MS;
    if (!revalidate && stores.length && fresh) return;

    if (inFlight) return inFlight;
    if (!stores.length) setLoading(true);
    setError(null);

    const p = (async () => {
      try {
        const res = await api.get(ENDPOINT);
        const data = Array.isArray(res.data) ? res.data : [];
        // (optional) preload logos to avoid pop-in
        await preloadLogos(data);
        setStores(data);
        writeCache(data);
        tsRef.current = Date.now();
      } catch (e) {
        if (!stores.length) setError(e?.message || "Failed to load stores");
      } finally {
        setLoading(false);
        inFlight = null;
      }
    })();

    inFlight = p;
    return p;
  }, [stores.length]);

  // On mount: show cache instantly; soft revalidate
  useEffect(() => {
    if (cached?.data) {
      fetchActiveStores({ revalidate: true });
    } else {
      fetchActiveStores();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = useCallback(() => fetchActiveStores({ revalidate: true }), [fetchActiveStores]);

  return (
    <ActiveStoresContext.Provider
      value={{
        activeStores: stores,
        loading,
        error,
        refetch,
        lastFetched: tsRef.current,
      }}
    >
      {children}
    </ActiveStoresContext.Provider>
  );
}

export function useActiveStores() {
  const ctx = useContext(ActiveStoresContext);
  if (!ctx) throw new Error("useActiveStores must be used within an ActiveStoresProvider");
  return ctx;
}
